import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MdOutlineRocketLaunch,
  MdOutlineTrendingUp,
  MdOutlineAutoAwesome,
  MdOutlineSchool,
  MdOutlineVerified,
  MdOutlineBuild,
  MdOutlineLightbulb,
  MdOutlineAdd,
  MdOutlineRemove,
} from 'react-icons/md';
import SeoHead from '../components/SeoHead';

const MEETING_LINK = 'https://meetings-eu1.hubspot.com/claus-moeldrup';

const tracks = [
  {
    id: 'fundament',
    intensity: 1,
    tag: 'Niveau 1',
    Icon: MdOutlineRocketLaunch,
    title: 'AI Fundament',
    tagline: 'Byg det rigtige fundament',
    hoursLabel: '1 uges konsulentressource / måned',
    price: '40.000 DKK',
    forWho: 'Passer til organisationer der vil i gang med AI på en struktureret og ansvarlig måde. Med løbende sparring, governance og de første konkrete resultater på plads.',
    accent: '#C03626',
    accentLight: '#FFF2EE',
    borderColor: '#F1D5CF',
  },
  {
    id: 'vaekst',
    intensity: 2,
    tag: 'Niveau 2',
    Icon: MdOutlineTrendingUp,
    title: 'AI Vækst',
    tagline: 'Skaler systematisk og dokumenteret',
    hoursLabel: '2 ugers konsulentressource / måned',
    price: '75.000 DKK',
    forWho: 'Passer til organisationer der vil bygge AI-kapabilitet bredt, med hurtigere implementering, change management og AI som en naturlig del af driften.',
    accent: '#1a6b4a',
    accentLight: '#f0faf5',
    borderColor: '#b8dece',
    featured: true,
  },
  {
    id: 'transformation',
    intensity: 3,
    tag: 'Niveau 3',
    Icon: MdOutlineAutoAwesome,
    title: 'AI Transformation',
    tagline: 'AI som strategisk konkurrencefordel',
    hoursLabel: 'Fuld måneds konsulentressource',
    price: '125.000 DKK',
    forWho: 'Passer til organisationer der er klar til at gøre AI til en kernekapabilitet. Det sker med en dedikeret AICE-ressource i organisationen og AI som drivkraft på tværs af hele virksomheden.',
    accent: '#1a1814',
    accentLight: '#f7f3ec',
    borderColor: '#d8d2c6',
  },
];

const services = [
  {
    Icon: MdOutlineRocketLaunch,
    title: 'AI-strategi & prioritering',
    desc: 'Vi kortlægger jeres forretning løbende, identificerer de mest værdifulde use cases og sikrer en klar, prioriteret retning der opdateres i takt med at AI-landskabet og jeres organisation udvikler sig.',
  },
  {
    Icon: MdOutlineAutoAwesome,
    title: 'AI-assistenter & automatisering',
    desc: 'Vi designer, bygger og itererer på AI-assistenter, workflows og multi-agent automatiseringer der løser konkrete opgaver i jeres hverdag. Fra sagsforberedelse og dokumentopsummering til kvalitetstjek og research.',
  },
  {
    Icon: MdOutlineSchool,
    title: 'Kompetencer & adoption',
    desc: 'Workshops, prompting-træning og løbende kompetenceudvikling sikrer at AI faktisk bruges, og at organisationen tager ejerskab. Vi arbejder med ledere, nøglepersoner og medarbejdere på alle niveauer.',
  },
  {
    Icon: MdOutlineVerified,
    title: 'Governance & sikkerhed',
    desc: 'Retningslinjer for ansvarlig brug, datasikkerhed og compliance-overvejelser der giver tryghed i ledelsen og i hele organisationen. Vi holder jer ajour, når regler og best practices udvikler sig.',
  },
  {
    Icon: MdOutlineBuild,
    title: 'Teknisk integration',
    desc: 'Datastrategi, API-integrationer og systemarkitektur der forbinder AI-løsningerne med jeres eksisterende data, systemer og platforme, uanset om I arbejder i Copilot, ChatGPT, Claude eller egne systemer.',
  },
  {
    Icon: MdOutlineLightbulb,
    title: 'Opfølgning & dokumentation',
    desc: 'ROI-tracking, månedlige statusrapporter og kvartalsvise ledelsespræsentationer der gør effekten synlig og dokumenterbar. I ved altid præcis, hvad pengene har givet.',
  },
];

const faqs = [
  {
    q: 'Hvad er forskellen på de tre niveauer?',
    a: 'Den eneste forskel er hvor mange konsulentdage vi investerer sammen hver måned. Alle tre niveauer har adgang til præcis samme faglige bredde og samme type leverancer. Det tager bare kortere eller længere tid at nå dertil. En AI-automatisering er ikke forbeholdt Niveau 3.',
  },
  {
    q: 'Kan vi starte på Niveau 1 og opgradere?',
    a: 'Ja, og det gør mange. Det er et naturligt forløb at starte på Niveau 1 for at lære organisationen og processerne at kende, og derefter skalere op når behovet vokser eller organisationen er klar til mere tempo.',
  },
  {
    q: 'Er der en bindingsperiode?',
    a: 'Nej. Vi tror på, at et godt samarbejde skal holde af sig selv. I kan opsige aftalen med én måneds varsel. Vi anbefaler dog at give samarbejdet mindst 3 måneder for at se de første resultater.',
  },
  {
    q: 'Hvad kræver det af vores interne ressourcer?',
    a: 'Typisk 2–4 timer om måneden fra en intern kontaktperson til briefs, sparring og godkendelser. Vi tilrettelægger arbejdet, så belastningen på jeres organisation er minimal. I behøver ikke forstå teknikken, bare kende jeres forretning.',
  },
  {
    q: 'Passer det til vores størrelse?',
    a: 'Vi arbejder med organisationer fra 15 til 500+ ansatte. Det vigtigste er ikke størrelsen, men at der er konkrete processer og ambitioner at arbejde med. Book et afklaringsmøde, og vi vurderer det sammen.',
  },
];

const IntensityDots = ({ level, accent }) => (
  <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
    {[1, 2, 3].map((i) => (
      <span key={i} style={{ width: '8px', height: '8px', borderRadius: '50%', background: i <= level ? accent : '#E8E0D8', display: 'inline-block' }} />
    ))}
  </div>
);

const AICenterOfExcellence = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div style={pageStyle}>
      <SeoHead
        title="AI Center of Excellence | AICE"
        description="En løbende partnerskabsaftale der bygger varig AI-kapabilitet i din organisation. Tre niveauer, månedlig betaling, ingen binding."
        canonicalPath="/ydelser/ai-center-of-excellence"
      />

      {/* HERO */}
      <section style={heroSection}>
        <p style={eyebrow}>Ydelser</p>
        <h1 style={heroTitle}>
          AI Center of<br />
          <span style={{ color: '#C03626' }}>Excellence</span>
        </h1>
        <p style={heroText}>
          En løbende partnerskabsaftale der bygger varig AI-kapabilitet i jeres organisation.
          Ikke et enkeltprojekt med en slutdato, men et dedikeret AICE-team der arbejder
          side om side med jer, måned efter måned.
        </p>
        <p style={heroSub}>Tre niveauer · Månedlig betaling · Ingen binding</p>
        <div style={ctaRow}>
          <a href={MEETING_LINK} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
            <button type="button" style={primaryCta}>Book et afklaringsmøde</button>
          </a>
          <Link to="/ydelser" style={{ textDecoration: 'none' }}>
            <button type="button" style={secondaryCta}>Se alle ydelser</button>
          </Link>
        </div>
      </section>

      {/* NIVEAU NAV */}
      <div style={trackStrip}>
        {tracks.map((t) => (
          <a key={t.id} href={`#${t.id}`} style={trackStripItem}>
            <IntensityDots level={t.intensity} accent={t.accent} />
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#5C392F' }}>{t.title}</span>
          </a>
        ))}
      </div>

      {/* NIVEAU CARDS */}
      <section style={tracksSection}>
        {tracks.map((track) => (
          <article
            key={track.id}
            id={track.id}
            style={{
              ...trackCard,
              borderColor: track.featured ? track.accent : track.borderColor,
              borderWidth: track.featured ? '2px' : '1.5px',
              boxShadow: track.featured ? '0 20px 48px rgba(26,107,74,0.12)' : '0 8px 28px rgba(0,0,0,0.06)',
            }}
          >
            {track.featured && (
              <div style={{ ...featuredBadge, background: track.accent }}>Anbefalt</div>
            )}
            <div style={trackHeader}>
              <div style={trackMeta}>
                <span style={{ ...trackTag, background: track.accentLight, color: track.accent }}>{track.tag}</span>
                <IntensityDots level={track.intensity} accent={track.accent} />
              </div>
              <div style={{ ...trackIconWrap, background: track.accentLight }}>
                <track.Icon style={{ fontSize: '1.4rem', color: track.accent }} />
              </div>
            </div>
            <h2 style={trackTitle}>{track.title}</h2>
            <p style={{ ...trackTagline, color: track.accent }}>{track.tagline}</p>
            <div style={{ ...priceBlock, borderColor: track.borderColor, background: track.accentLight }}>
              <div style={priceMain}>
                <span style={{ ...priceAmount, color: track.accent }}>{track.price}</span>
                <span style={priceMonth}>/måned</span>
              </div>
              <span style={priceNote}>{track.hoursLabel} · ekskl. moms</span>
            </div>
            <p style={trackForWho}>{track.forWho}</p>
          </article>
        ))}
      </section>

      {/* SCOPE CALLOUT */}
      <section style={scopeSection}>
        <div style={scopeInner}>
          <div style={scopeText}>
            <p style={{ ...eyebrow, color: 'rgba(255,255,255,0.6)' }}>Samme scope. Forskelligt tempo.</p>
            <h2 style={scopeTitle}>Alle niveauer indeholder alt. Niveauet bestemmer, hvor hurtigt vi når dertil</h2>
            <p style={scopeDesc}>
              En AI-automatisering, en datastrategi eller et kompetenceløft er ikke forbeholdt
              de dyreste niveauer. Scope er det samme. Det der varierer er antallet af
              konsulentdage vi investerer hver måned, og dermed hastigheden.
              Niveau 1 tager bare lidt længere tid end Niveau 3.
            </p>
          </div>
          <div style={scopeStats}>
            {[
              { n: '1 uge', l: 'Niveau 1', sub: 'pr. måned' },
              { n: '2 uger', l: 'Niveau 2', sub: 'pr. måned' },
              { n: '4 uger', l: 'Niveau 3', sub: 'pr. måned' },
            ].map(({ n, l, sub }) => (
              <div key={l} style={scopeStat}>
                <span style={scopeStatN}>{n}</span>
                <span style={scopeStatL}>{l}</span>
                <span style={scopeStatSub}>{sub}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section style={servicesSection}>
        <div style={sectionHeader}>
          <p style={eyebrowSmall}>Hvad vi arbejder med</p>
          <h2 style={sectionTitle}>Alt dette indgår, uanset niveau</h2>
          <p style={sectionSubtext}>
            Vi dækker hele spektret fra strategi til implementering. I behøver ikke
            vide, hvad I har brug for. Det finder vi ud af løbende.
          </p>
        </div>
        <div style={servicesGrid}>
          {services.map(({ Icon, title, desc }) => (
            <div key={title} style={serviceCard}>
              <div style={serviceIconWrap}>
                <Icon style={{ fontSize: '1.4rem', color: '#C03626' }} />
              </div>
              <h3 style={serviceTitle}>{title}</h3>
              <p style={serviceDesc}>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section style={howSection}>
        <div style={howInner}>
          <div style={sectionHeader}>
            <p style={eyebrowSmall}>Sådan fungerer det</p>
            <h2 style={sectionTitle}>Et samarbejde, der er enkelt at komme i gang med</h2>
          </div>
          <div style={howSteps}>
            {[
              { n: '01', title: 'Afklaringsmøde', desc: 'Vi mødes i 20–30 minutter. Ingen forberedelse nødvendig, vi stiller spørgsmålene. Vi finder frem til det niveau og de fokusområder, der giver bedst mening for jer.' },
              { n: '02', title: 'Opstartsworkshop', desc: 'Vi kickstarter samarbejdet med en workshop der kortlægger jeres processer, ambitioner og lavthængende frugter. I går hjem med en klar plan for de første måneder.' },
              { n: '03', title: 'Månedlig kadence', desc: 'Hver måned arbejder vi målrettet på de aftalte prioriteter, med løbende statusopdateringer og en fast sparringssession, så I altid ved, hvad der sker.' },
              { n: '04', title: 'Løbende optimering', desc: 'AI-landskabet ændrer sig hurtigt. Vi holder jer ajour, justerer retningen og sikrer at I altid er et skridt foran og ikke bagefter.' },
            ].map(({ n, title, desc }) => (
              <div key={n} style={howStep}>
                <span style={howStepN}>{n}</span>
                <h3 style={howStepTitle}>{title}</h3>
                <p style={howStepDesc}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={faqSection}>
        <h2 style={{ ...sectionTitle, textAlign: 'center', marginBottom: '2rem' }}>Ofte stillede spørgsmål</h2>
        <div style={faqList}>
          {faqs.map((item, i) => {
            const isOpen = openFaq === i;
            return (
              <div key={i} style={faqItem}>
                <button type="button" style={faqQuestion} onClick={() => setOpenFaq(isOpen ? null : i)}>
                  <span>{item.q}</span>
                  {isOpen
                    ? <MdOutlineRemove style={{ fontSize: '1.3rem', color: '#C03626', flexShrink: 0 }} />
                    : <MdOutlineAdd style={{ fontSize: '1.3rem', color: '#C03626', flexShrink: 0 }} />
                  }
                </button>
                {isOpen && <p style={faqAnswer}>{item.a}</p>}
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section style={ctaSection}>
        <h2 style={ctaTitle}>Ikke sikker på, hvilket niveau der passer jer?</h2>
        <p style={ctaText}>
          Det er vi. Book et gratis 20-minutters afklaringsmøde. Ingen salgspitch,
          ingen forberedelse. Bare en ærlig snak om, hvor I er, og hvad der giver mening.
        </p>
        <a href={MEETING_LINK} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
          <button type="button" style={ctaButton}>Book gratis afklaringsmøde</button>
        </a>
      </section>
    </div>
  );
};

export default AICenterOfExcellence;

// ── Styles ──────────────────────────────────────────────

const pageStyle = {
  background: 'linear-gradient(180deg, #FFF4F0 0%, #FFFFFF 30%)',
  minHeight: '100vh',
  color: '#1f1f1f',
};

const eyebrow = {
  textTransform: 'uppercase',
  letterSpacing: '0.2em',
  color: '#C03626',
  fontSize: '0.85rem',
  margin: 0,
};

const eyebrowSmall = { ...eyebrow, marginBottom: '0.4rem' };

const heroSection = {
  maxWidth: '760px',
  margin: '0 auto',
  padding: '5rem clamp(1.5rem, 5vw, 3rem) 2.5rem',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '1rem',
};

const heroTitle = {
  fontSize: 'clamp(2.4rem, 6vw, 4rem)',
  fontWeight: 800,
  color: '#1f1f1f',
  lineHeight: 1.05,
  margin: 0,
};

const heroText = {
  fontSize: '1.1rem',
  color: '#5C392F',
  lineHeight: 1.7,
  maxWidth: '580px',
  margin: 0,
};

const heroSub = {
  fontSize: '0.9rem',
  color: '#8C5A50',
  letterSpacing: '0.04em',
  margin: 0,
};

const ctaRow = {
  display: 'flex',
  gap: '1rem',
  flexWrap: 'wrap',
  justifyContent: 'center',
  marginTop: '0.5rem',
};

const primaryCta = {
  background: 'linear-gradient(135deg, #C03626, #FF6F61)',
  color: '#fff',
  border: 'none',
  borderRadius: '10px',
  padding: '0.85rem 1.75rem',
  fontWeight: 700,
  fontSize: '1rem',
  cursor: 'pointer',
  boxShadow: '0 10px 24px rgba(192,54,38,0.25)',
};

const secondaryCta = {
  background: '#fff',
  color: '#C03626',
  border: '1px solid #C03626',
  borderRadius: '10px',
  padding: '0.85rem 1.75rem',
  fontWeight: 700,
  fontSize: '1rem',
  cursor: 'pointer',
};

// Niveau nav strip
const trackStrip = {
  display: 'flex',
  justifyContent: 'center',
  gap: '0.5rem',
  flexWrap: 'wrap',
  padding: '0 clamp(1rem, 4vw, 3rem) 2rem',
  maxWidth: '900px',
  margin: '0 auto',
};

const trackStripItem = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.5rem 1rem',
  background: '#fff',
  border: '1px solid #F1D5CF',
  borderRadius: '30px',
  textDecoration: 'none',
};

// Niveau cards
const tracksSection = {
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '0 clamp(1rem, 4vw, 3rem) 4rem',
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: '1.5rem',
  alignItems: 'start',
};

const trackCard = {
  background: '#fff',
  borderRadius: '20px',
  padding: '2rem',
  borderStyle: 'solid',
  display: 'flex',
  flexDirection: 'column',
  gap: '1.1rem',
  position: 'relative',
};

const featuredBadge = {
  position: 'absolute',
  top: '-14px',
  left: '50%',
  transform: 'translateX(-50%)',
  color: '#fff',
  fontSize: '0.72rem',
  fontWeight: 700,
  padding: '3px 14px',
  borderRadius: '20px',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
};

const trackHeader = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const trackMeta = { display: 'flex', alignItems: 'center', gap: '0.75rem' };

const trackTag = {
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  padding: '3px 10px',
  borderRadius: '20px',
};

const trackIconWrap = {
  width: '44px',
  height: '44px',
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const trackTitle = {
  fontSize: '1.6rem',
  fontWeight: 800,
  color: '#1f1f1f',
  margin: 0,
  lineHeight: 1.1,
};

const trackTagline = {
  fontSize: '0.88rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  margin: 0,
};

const priceBlock = {
  borderRadius: '10px',
  padding: '1rem 1.25rem',
  borderStyle: 'solid',
  borderWidth: '1px',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.3rem',
};

const priceMain = { display: 'flex', alignItems: 'baseline', gap: '0.2rem' };

const priceAmount = { fontSize: '1.8rem', fontWeight: 800, lineHeight: 1 };

const priceMonth = { fontSize: '1rem', fontWeight: 500, color: '#8C5A50' };

const priceNote = { fontSize: '0.78rem', color: '#8C5A50', fontStyle: 'italic' };

const trackForWho = {
  fontSize: '0.95rem',
  color: '#5C392F',
  lineHeight: 1.7,
  margin: 0,
};

// Scope callout
const scopeSection = {
  background: 'linear-gradient(135deg, #C03626 0%, #8a2419 100%)',
  padding: 'clamp(3rem, 6vw, 5rem) clamp(1.5rem, 5vw, 4rem)',
  marginBottom: '4rem',
};

const scopeInner = {
  maxWidth: '1100px',
  margin: '0 auto',
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: '3rem',
  alignItems: 'center',
};

const scopeText = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
};

const scopeTitle = {
  fontSize: 'clamp(1.4rem, 3vw, 2rem)',
  fontWeight: 800,
  color: '#fff',
  margin: 0,
  lineHeight: 1.2,
};

const scopeDesc = {
  fontSize: '0.97rem',
  color: 'rgba(255,255,255,0.75)',
  lineHeight: 1.75,
  margin: 0,
};

const scopeStats = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '1px',
  background: 'rgba(255,255,255,0.15)',
  borderRadius: '12px',
  overflow: 'hidden',
};

const scopeStat = {
  background: 'rgba(255,255,255,0.08)',
  padding: '1.5rem 1rem',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0.3rem',
  textAlign: 'center',
};

const scopeStatN = {
  fontSize: '1.6rem',
  fontWeight: 800,
  color: '#fff',
  lineHeight: 1,
};

const scopeStatL = {
  fontSize: '0.82rem',
  fontWeight: 600,
  color: 'rgba(255,255,255,0.85)',
};

const scopeStatSub = {
  fontSize: '0.72rem',
  color: 'rgba(255,255,255,0.5)',
  fontStyle: 'italic',
};

// Services
const servicesSection = {
  maxWidth: '1100px',
  margin: '0 auto',
  padding: '0 clamp(1rem, 4vw, 3rem) 5rem',
};

const sectionHeader = {
  marginBottom: '2.5rem',
};

const sectionTitle = {
  fontSize: 'clamp(1.5rem, 3vw, 2.2rem)',
  fontWeight: 800,
  color: '#1f1f1f',
  margin: '0 0 0.5rem',
};

const sectionSubtext = {
  fontSize: '1.05rem',
  color: '#5C392F',
  lineHeight: 1.65,
  margin: 0,
  maxWidth: '600px',
};

const servicesGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: '1.25rem',
};

const serviceCard = {
  background: '#fff',
  border: '1px solid #F1D5CF',
  borderRadius: '16px',
  padding: '1.75rem',
  boxShadow: '0 6px 18px rgba(192,54,38,0.06)',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
};

const serviceIconWrap = {
  width: '44px',
  height: '44px',
  borderRadius: '12px',
  background: '#FFF2EE',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const serviceTitle = {
  fontSize: '1.05rem',
  fontWeight: 700,
  color: '#1f1f1f',
  margin: 0,
};

const serviceDesc = {
  fontSize: '0.92rem',
  color: '#5C392F',
  lineHeight: 1.7,
  margin: 0,
};

// How it works
const howSection = {
  background: '#FFF9F8',
  borderTop: '1px solid #F1D5CF',
  borderBottom: '1px solid #F1D5CF',
  padding: 'clamp(3rem, 6vw, 5rem) clamp(1.5rem, 5vw, 4rem)',
  marginBottom: '4rem',
};

const howInner = {
  maxWidth: '1100px',
  margin: '0 auto',
};

const howSteps = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: '1.5rem',
};

const howStep = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.6rem',
};

const howStepN = {
  fontSize: '1.8rem',
  fontWeight: 800,
  color: '#FFD6CC',
  lineHeight: 1,
};

const howStepTitle = {
  fontSize: '1.05rem',
  fontWeight: 700,
  color: '#1f1f1f',
  margin: 0,
};

const howStepDesc = {
  fontSize: '0.92rem',
  color: '#5C392F',
  lineHeight: 1.65,
  margin: 0,
};

// FAQ
const faqSection = {
  maxWidth: '720px',
  margin: '0 auto',
  padding: '0 clamp(1rem, 4vw, 3rem) 5rem',
};

const faqList = { display: 'flex', flexDirection: 'column' };

const faqItem = { borderBottom: '1px solid #F1D5CF' };

const faqQuestion = {
  width: '100%',
  background: 'none',
  border: 'none',
  padding: '1.25rem 0',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '1rem',
  fontSize: '1rem',
  fontWeight: 600,
  color: '#1f1f1f',
  cursor: 'pointer',
  textAlign: 'left',
};

const faqAnswer = {
  fontSize: '0.95rem',
  color: '#5C392F',
  lineHeight: 1.7,
  margin: '0 0 1.25rem',
};

// CTA
const ctaSection = {
  background: 'linear-gradient(135deg, #C03626 0%, #8a2419 100%)',
  color: '#fff',
  textAlign: 'center',
  padding: 'clamp(3.5rem, 8vw, 6rem) clamp(1.5rem, 5vw, 4rem)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '1rem',
};

const ctaButton = {
  background: '#fff',
  color: '#C03626',
  border: 'none',
  borderRadius: '10px',
  padding: '0.85rem 1.75rem',
  fontWeight: 700,
  fontSize: '1rem',
  cursor: 'pointer',
  boxShadow: '0 10px 24px rgba(0,0,0,0.15)',
};

const ctaTitle = {
  fontSize: 'clamp(1.6rem, 4vw, 2.4rem)',
  fontWeight: 800,
  margin: 0,
};

const ctaText = {
  fontSize: '1.05rem',
  color: 'rgba(255,255,255,0.8)',
  maxWidth: '520px',
  lineHeight: 1.65,
  margin: 0,
};
